import React, { FC, memo, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { useI18n, useLogger, useSelector } from '~Hooks';
import { Locale } from '~Types';
import seIcon from '~Scenes/SelfServiceKiosks/Assets/images/Swedish.svg';
import enIcon from '~Scenes/SelfServiceKiosks/Assets/images/English.svg';
import noIcon from '~Scenes/SelfServiceKiosks/Assets/images/Norwegian.svg';
import dkIcon from '~Scenes/SelfServiceKiosks/Assets/images/Danish.svg';
import esIcon from '~Scenes/SelfServiceKiosks/Assets/images/Spanish.svg';
import trIcon from '~Scenes/SelfServiceKiosks/Assets/images/Turkish.svg';
import LanguageButton from './LanguageButton';

/** The venue country's own language, by country code - the same table the kiosk uses. */
const LOCAL_LANGUAGE: Record<string, { code: Locale; title: string; icon: string }> = {
  SE: { code: 'se', title: 'Svenska', icon: seIcon },
  NO: { code: 'no', title: 'Norsk', icon: noIcon },
  DK: { code: 'dk', title: 'Dansk', icon: dkIcon },
  ES: { code: 'es', title: 'Español', icon: esIcon },
  TR: { code: 'tr', title: 'Türkçe', icon: trIcon },
  EN: { code: 'en', title: 'English', icon: enIcon },
};

const ENGLISH = { code: 'en' as Locale, title: 'English', icon: enIcon };

/** Two small pills in a row; the active one is filled. */
const Group = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

/**
 * Local language and English, side by side. Switching goes through the
 * i18n hook, which stores the choice - every translated string on screen
 * re-renders, which is the point, so nothing here tries to avoid that.
 */
const LanguageSwitch: FC = () => {
  const { locale, changeLanguage } = useI18n();
  const logger = useLogger('epos-language');
  const country = useSelector((state) => state.country);

  const languages = useMemo(() => {
    const local = LOCAL_LANGUAGE[country ?? ''] ?? LOCAL_LANGUAGE.SE;
    return local.code === ENGLISH.code ? [ENGLISH] : [local, ENGLISH];
  }, [country]);

  const handleSelect = useCallback(
    (code: Locale) => {
      logger.info('Language changed on welcome screen', { locale: code });
      changeLanguage(code);
    },
    [changeLanguage],
  );

  if (languages.length < 2) {
    return null;
  }

  return (
    <Group data-testid="epos-language-switch">
      {languages.map((language) => (
        <LanguageButton
          key={language.code}
          code={language.code}
          title={language.title}
          icon={language.icon}
          active={locale === language.code}
          onSelect={handleSelect}
        />
      ))}
    </Group>
  );
};

export default memo(LanguageSwitch);
